// /datasets/:id/provenance — the dataset's upstream and downstream chain
// (GET /api/datasets/{id}/provenance, services/provenance.py): datasets are nodes, analyses are
// edges labelled with their instrument, protocol files hang off the datasets that hold them. Every
// dataset links to its own provenance page, so the chain can be followed from any dataset.
//
// The editors of a derived dataset (its study's author, the project's curators, admins) record the analysis that produced it here
// (provenance/AnalysisForm.jsx); a derived dataset without one says "No analysis recorded".
import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../api";
import { Empty, ErrorNote, Loading, SectionTitle } from "../../components/Feedback";
import { Badge, Btn, Card } from "../../components/Shared";
import { useApi } from "../../useApi";
import { authorName } from "./author";
import AnalysisForm from "./provenance/AnalysisForm";
import { analysisLabel, buildProvenanceDiagram } from "./provenance/provenanceDiagram";

// Lazy, as DbDesignPage loads SchemaDiagram: mermaid is large and ESM-only.
const ProvenanceDiagram = lazy(() => import("./provenance/ProvenanceDiagram"));

function DatasetLink({ node }) {
  // data-tap: a 44 px target on a phone (a list of these is a row of controls, not a sentence).
  return <Link data-tap to={`/datasets/${node.id}/provenance`}>{node.name}</Link>;
}

function ProvenanceView({ dataset, canEdit }) {
  const graph = useApi(`/datasets/${dataset.id}/provenance`);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(null);     // null | "new" | AnalysisOut
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [error, setError] = useState(null);
  const g = graph.data;
  const spec = useMemo(() => (g ? buildProvenanceDiagram(g) : ""), [g]);
  const open = useCallback(id => navigate(`/datasets/${id}/provenance`), [navigate]);

  if (graph.loading && !g) return <Loading />;
  if (graph.error) return <ErrorNote error={graph.error} />;
  if (!g) return null;

  const nodes = Object.fromEntries(g.nodes.map(n => [n.id, n]));
  const root = nodes[dataset.id] || { produced_by: [] };
  const producers = g.analyses.filter(a => root.produced_by.includes(a.id));
  const derived = dataset.kind === "derived";

  const edit = async a => {
    setError(null);
    setLoadingAnalysis(true);
    try {
      setEditing(await api.get(`/analyses/${a.id}`));
    } catch (e) {
      setError(e);
    } finally {
      setLoadingAnalysis(false);
    }
  };
  const saved = () => { setEditing(null); graph.reload(); };

  const names = ids => ids.map((id, i) => (
    <span key={id}>{i > 0 && ", "}{nodes[id] ? <DatasetLink node={nodes[id]} /> : `dataset ${id}`}</span>
  ));

  return (
    <div>
      {derived && producers.length === 0 && (
        <Card style={{ marginBottom: 16, padding: "14px 20px" }}>
          <p role="status" style={{ margin: 0, fontSize: 14 }}>
            <strong>No analysis recorded.</strong> This dataset is derived, but nothing says which inputs,
            software and parameters produced it.{canEdit ? " Record the analysis below." : ""}
          </p>
        </Card>
      )}
      {!derived && producers.length === 0 && (
        <p style={{ fontSize: 13, color: "#4b5563", marginTop: 0 }}>
          A primary dataset: measured or collected, not produced by an analysis.
        </p>
      )}

      {canEdit && derived && editing === null && (
        <div style={{ marginBottom: 16 }}>
          <Btn onClick={() => setEditing("new")}>Record analysis</Btn>
        </div>
      )}
      <ErrorNote error={error} />
      {loadingAnalysis && <Loading label="Loading the analysis…" />}
      {editing !== null && (
        <AnalysisForm dataset={dataset} analysis={editing === "new" ? null : editing}
                      onSaved={saved} onCancel={() => setEditing(null)} />
      )}

      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>Provenance graph</SectionTitle>
        {g.nodes.length <= 1 && g.analyses.length === 0
          ? <Empty>No analysis links this dataset to another one yet.</Empty>
          : (
            <figure style={{ margin: 0 }}>
              <Suspense fallback={<Loading label="Drawing the graph…" />}>
                <ProvenanceDiagram spec={spec} onOpen={open} />
              </Suspense>
              <figcaption style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                Datasets are boxes (derived ones rounded; this one bold, dashed when you may not edit it),
                arrows are analyses labelled with their software, hexagons are protocol files. Click a dataset,
                or use the lists below, to follow the chain.
                {g.truncated && ` Only ${g.depth_limit} analyses in each direction are shown.`}
              </figcaption>
            </figure>
          )}
      </Card>

      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>Datasets in this graph</SectionTitle>
        <ul aria-label="Datasets in this graph" style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.8 }}>
          {g.nodes.map(n => (
            <li key={n.id}>
              {n.root ? <strong>{n.name} (this dataset)</strong> : <DatasetLink node={n} />}{" "}
              <Badge color={n.kind === "derived" ? "amber" : "gray"}>{n.kind}</Badge>{" "}
              <span style={{ color: "#6b7280", fontSize: 13 }}>
                {n.study_name} · by {authorName(n.created_by)}{!n.can_edit && " · read-only for you"}
              </span>
              {n.protocols.length > 0 && (
                <span style={{ fontSize: 13 }}>
                  {" "}· protocols: {n.protocols.map(p => p.filename).join(", ")}
                </span>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <SectionTitle>Analyses</SectionTitle>
        {g.analyses.length === 0 ? <Empty>No analyses.</Empty> : (
          <ul aria-label="Analyses in this graph" style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.8 }}>
            {g.analyses.map(a => (
              <li key={a.id}>
                <strong>{a.name}</strong> — {analysisLabel(a)}
                {a.objects.length > 0 && <> · from {names(a.objects)}</>}
                {a.results.length > 0 && <> · to {names(a.results)}</>}
                {a.protocol_filename && <> · protocol {a.protocol_filename}</>}
                {a.agent_name && <> · by {a.agent_name}</>}
                {a.can_edit && root.produced_by.includes(a.id) && editing === null && (
                  <span style={{ marginLeft: 8 }}>
                    <Btn variant="secondary" onClick={() => edit(a)} style={{ padding: "3px 10px", fontSize: 12 }}>
                      Edit {a.name}
                    </Btn>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// Keyed by the dataset: following the chain to another dataset starts with a closed form.
export default function ProvenanceTab(props) {
  return <ProvenanceView key={props.dataset.id} {...props} />;
}
